import {
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartJSTooltip,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartSkeleton } from '../../../../abelcommon/components/ChartSkeleton';
import { loadBalancerAPI } from '../../../../api/load-balancer';
import { formatThoundsandNumber, stableHash, ThemeMode } from '../../../../abelcommon';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartJSTooltip,
  Filler,
  Legend,
  TimeScale,
  zoomPlugin
);

enum HashRateTimeRange {
  OneMonth = '1M',
  SixMonth = '6M',
  OneYear = '1Y',
  All = 'all',
}

interface HashRateDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AllHashRateChartDialog = ({ open, onClose }: HashRateDialogProps) => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<HashRateTimeRange>(HashRateTimeRange.OneMonth);
  const {
    data: poolHashRateData,
    isLoading: poolHashRateLoading,
    error: poolHashRateError,
  } = loadBalancerAPI.hookWrapperQuery.useGetHashRatesChart(timeRange, `max-pool-service.abelian.info`);

  const { options, data } = useMemo(() => {
    if (poolHashRateLoading || !poolHashRateData || poolHashRateData.length === 0) return {};
    const sortedUsersByTime = poolHashRateData.map((poolHashRateData) => ({
      ...poolHashRateData,
      time: new Date(poolHashRateData.createdAt).getTime() || null,
      numbers: parseInt(formatThoundsandNumber(stableHash(poolHashRateData.hashRate * 1000))),
    }));

    sortedUsersByTime.sort((a, b) => a.time - b.time);

    const options: ChartOptions<'line'> = {
      parsing: false,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: theme.palette.text.secondary,
          },
        },
        x: {
          bounds: 'data',
          type: 'time',
          offset: false,
          ticks: {
            maxTicksLimit: 10,
            color: theme.palette.text.secondary,
          },
          time: {
            unit: 'day',
            displayFormats: {
              day: 'yyyy-MM-dd',
            },
          },
        },
      },
      plugins: {
        tooltip: {
          intersect: false,
          filter: () => true,
          callbacks: {
            label: function (context) {
              const { numbers } = sortedUsersByTime[context.dataIndex];
              return [`Hash rate: ${numbers} GH/s`];
            },
          },
        },
        legend: {
          position: 'top',
        },
        title: {
          display: true,
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x',
          },
          pan: {
            enabled: true,
            mode: 'x',
          },
          limits: {
            x: {
              min: sortedUsersByTime[0].time,
              max: sortedUsersByTime[sortedUsersByTime.length - 1].time,
            },
          },
        },
      },
    };

    const data = {
      labels: sortedUsersByTime.map(({ time }) => time),
      datasets: [
        {
          fill: true,
          label: 'Hash Rates',
          data: sortedUsersByTime.map(({ numbers, time }) => ({
            x: time,
            y: numbers,
          })),
          borderColor: theme.palette.appBlue.lightest,
          backgroundColor: 'rgb(37,150,190, 0.5)',
          pointRadius: 3,
          pointBackgroundColor: '#FFFFFF',
          pointBorderColor: theme.palette.appBlue.lightest,
        },
      ],
    };
    return {
      options,
      data,
    };
  }, [theme.palette.appBlue.lightest, theme.palette.text.secondary, poolHashRateData, poolHashRateLoading]);

  const chartFilterButtonProps = useMemo(
    () => [
      {
        value: HashRateTimeRange.OneMonth,
        label: '1M',
      },
      {
        value: HashRateTimeRange.SixMonth,
        label: '6M',
      },
      {
        value: HashRateTimeRange.OneYear,
        label: '1Y',
      },
      {
        value: HashRateTimeRange.All,
        label: 'All',
      },
    ],
    []
  );

  const apiLoading = poolHashRateLoading;
  const apiError = poolHashRateError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e4e4e4' }}>
        <DialogTitle>Hash Rates</DialogTitle>
        <DialogActions>
          <ToggleButtonGroup
            style={{ paddingRight: '20px' }}
            sx={theme.palette.mode === ThemeMode.Dark ? { boxShadow: 1 } : undefined}
            color="primary"
            value={timeRange}
            exclusive
            onChange={(e, newTimeRange) => {
              if (newTimeRange !== null) { 
                setTimeRange(newTimeRange);
              }
            }}
          >
            {chartFilterButtonProps.map(({ value, label }) => (
              <ToggleButton key={label} value={value} sx={{ borderColor: theme.palette.appGray.dark }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {label}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </DialogActions>
      </div>
      <DialogContent sx={{ overflow: 'auto', height: '500px' }}>
        {apiError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {apiError?.message}
          </Alert>
        ) : apiLoading || !options || !data ? (
          <ChartSkeleton />
        ) : (
          <Line options={options} data={data} />
        )}
      </DialogContent>
    </Dialog>
  );
};
